import {
  isOpenCopilotChatConsumerAvailable,
  publishOpenCopilotChat,
  subscribeAddCopilotChatReference,
  subscribeOpenCopilotChat,
  subscribeOpenCopilotChatConsumerAvailability,
} from '@github-ui/copilot-chat-events'
import {
  copilotChatHeaderButtonID,
  copilotDockedPageEndedEvent,
  copilotDockedPageReadyEvent,
  getDockedPanelOwner,
} from '@github-ui/copilot-chat-helpers/constants'
import {clearDockedPageReady, publishDockedPageReady} from '@github-ui/copilot-chat-helpers/docked-page-store'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {useEffect, useEffectEvent, useRef} from 'react'

import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useDockedPanelMetadata,
  useDockedPanelOpenRevision,
  useEnabledDockedPanels,
  useIsDockedPanelOpen,
} from '../contexts/DockedPanelContext'

const COPILOT_CHAT_URL_RESTORE_ID = 'copilot-chat-url-restore'

function getReturnFocusTarget(id?: string) {
  if (id === COPILOT_CHAT_URL_RESTORE_ID) return undefined

  const persistentLauncher =
    id === copilotChatHeaderButtonID ? document.getElementById(copilotChatHeaderButtonID) : null
  if (persistentLauncher instanceof HTMLElement) {
    return persistentLauncher.checkVisibility() ? persistentLauncher : null
  }

  const eventTrigger = id ? document.getElementById(id) : null
  if (eventTrigger) return eventTrigger

  return document.activeElement instanceof HTMLElement ? document.activeElement : undefined
}

export function useCopilotDockedPanel({isOnSupportedRoute}: {isOnSupportedRoute: boolean}) {
  const enabledPanels = useEnabledDockedPanels()
  const isCopilotChatDockable = isOnSupportedRoute && enabledPanels.includes(DockedPanel.CopilotChat)
  const isCopilotPanelOpen = useIsDockedPanelOpen(DockedPanel.CopilotChat)
  const metadata = useDockedPanelMetadata()
  const openRevision = useDockedPanelOpenRevision()
  const actions = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const handledUrlRestoreRevisionRef = useRef<number | null>(null)
  const openCopilotPanel = useEffectEvent((id?: string) => {
    const returnFocusTo = getReturnFocusTarget(id)
    if (!isCopilotPanelOpen) {
      actions.openPanel(DockedPanel.CopilotChat, {returnFocusTo})
    } else if (returnFocusTo) {
      returnFocusRef.current = returnFocusTo
    }
  })

  // Signal to embedded chat that this page owns a docked surface even while the
  // panel is closed, so it does not render a competing floating panel.
  useEffect(() => {
    if (!isCopilotChatDockable) return

    publishDockedPageReady()
    document.dispatchEvent(new CustomEvent(copilotDockedPageReadyEvent))
    return () => {
      clearDockedPageReady()
      document.dispatchEvent(new CustomEvent(copilotDockedPageEndedEvent))
    }
  }, [isCopilotChatDockable])

  // The minimized Restore control can outlive the PR toolbar trigger, so this
  // integration owns event-based opening and return-focus selection.
  useEffect(() => {
    if (!isCopilotChatDockable) return

    const unsubscribeOpen = subscribeOpenCopilotChat(event => {
      // Quick chat owns referenced payloads, but the PR surface still owns the dock.
      openCopilotPanel(event.payload?.id)
    })
    const unsubscribeAddReference = subscribeAddCopilotChatReference(event => {
      if (event.openPanel && getDockedPanelOwner() !== 'chatapp') openCopilotPanel(event.id)
    })
    return () => {
      unsubscribeOpen()
      unsubscribeAddReference()
    }
  }, [isCopilotChatDockable])

  // A URL restore opens the generic panel shell first. Replay the Copilot open
  // only after the separately mounted global consumer is ready.
  useEffect(() => {
    if (
      !isCopilotChatDockable ||
      !isCopilotPanelOpen ||
      !metadata?.restoredFromUrl ||
      handledUrlRestoreRevisionRef.current === openRevision
    ) {
      return
    }

    const openRestoredCopilotChat = () => {
      if (handledUrlRestoreRevisionRef.current === openRevision || !isOpenCopilotChatConsumerAvailable()) {
        return
      }
      handledUrlRestoreRevisionRef.current = openRevision
      publishOpenCopilotChat({
        id: COPILOT_CHAT_URL_RESTORE_ID,
        intent: CopilotChatIntents.conversation,
      })
    }

    openRestoredCopilotChat()
    return subscribeOpenCopilotChatConsumerAvailability(openRestoredCopilotChat)
  }, [isCopilotChatDockable, isCopilotPanelOpen, metadata?.restoredFromUrl, openRevision])
}
